import { useCallback, useRef } from 'react'

interface TouchPosition {
  x: number
  y: number
}

interface ViewboxPosition {
  x: number
  y: number
}

interface SingleTouchDragConfig {
  dragFactor: number
  onViewboxChange: (x: number, y: number) => void
  currentViewbox: ViewboxPosition
}

interface SingleTouchDragReturn {
  handleSingleTouchDrag: (touchEvent: React.TouchEvent) => void
  initializeTouchDrag: (touchEvent: React.TouchEvent) => void
  resetTouchDrag: () => void
}

const exceedsThreshold = (start: number, current: number, threshold: number): boolean =>
  Math.abs(start - current) > threshold

const calculateDelta = (start: TouchPosition, current: TouchPosition) => ({
  deltaX: start.x - current.x,
  deltaY: start.y - current.y
})

export const useSingleTouchDrag = ({
  dragFactor,
  onViewboxChange,
  currentViewbox
}: SingleTouchDragConfig): SingleTouchDragReturn => {
  const touchStartRef = useRef<TouchPosition | null>(null)
  const initialViewboxRef = useRef<ViewboxPosition | null>(null)

  const initializeTouchDrag = useCallback(
    (touchEvent: React.TouchEvent) => {
      const touch = touchEvent.touches[0]
      touchStartRef.current = { x: touch.clientX, y: touch.clientY }
      initialViewboxRef.current = { x: currentViewbox.x, y: currentViewbox.y }
    },
    [currentViewbox.x, currentViewbox.y]
  )

  const handleSingleTouchDrag = useCallback(
    (touchEvent: React.TouchEvent) => {
      if (!touchStartRef.current || !initialViewboxRef.current) return

      const currentTouch = touchEvent.touches[0]
      const currentPosition = { x: currentTouch.clientX, y: currentTouch.clientY }
      const { deltaX, deltaY } = calculateDelta(touchStartRef.current, currentPosition)

      let newX = currentViewbox.x
      let newY = currentViewbox.y
      let hasChanged = false

      if (exceedsThreshold(touchStartRef.current.x, currentPosition.x, dragFactor)) {
        newX = initialViewboxRef.current.x + deltaX
        hasChanged = true
      }

      if (exceedsThreshold(touchStartRef.current.y, currentPosition.y, dragFactor)) {
        newY = initialViewboxRef.current.y + deltaY
        hasChanged = true
      }

      if (hasChanged) {
        onViewboxChange(newX, newY)
      }
    },
    [dragFactor, currentViewbox.x, currentViewbox.y, onViewboxChange]
  )

  const resetTouchDrag = useCallback(() => {
    touchStartRef.current = null
    initialViewboxRef.current = null
  }, [])

  return {
    handleSingleTouchDrag,
    initializeTouchDrag,
    resetTouchDrag
  }
}